// Web Audio API Sound Engine & Calm Ambient Music for KALAS Nordic Luxury Distillery
// Organic acoustic sound design: warm Rhodes/celeste tones, gentle evolving harmonies,
// and soft melodic motifs inspired by Nordic stillness (Nils Frahm / Brian Eno aesthetic).

let audioCtx = null;
let masterGain = null;
let musicGain = null;
let sfxGain = null;

// Music state
let isMusicPlaying = false;
let musicScheduleTimer = null;
let melodyScheduleTimer = null;
let currentHarmonicStep = 0;
let activeMusicNodes = [];

// Interaction state tracking
let lastHoverTime = 0;
let lastSurfingTime = 0;
let hoverToneIndex = 0;

// Subscribed listeners for sound state changes
const listeners = new Set();

// Master volume calibration (calibrated with SFX +15% louder)
const MASTER_VOLUME = 0.20;
const SFX_VOLUME = 0.23; // +15% louder UI sound effects
const MUSIC_VOLUME = 0.20;

// Harmonic Chords Progression (Clean, open, soothing Nordic intervals with warm voicings)
// Voiced cleanly without clashing low-end detuning to eliminate any buzzing/drone fatigue.
const NORDIC_HARMONIES = [
  // 1. Dm9 / F (Deep serenity & copper warmth)
  {
    bass: 73.42, // D2
    pad: [146.83, 220.00, 261.63, 329.63], // D3, A3, C4, E4
    melodyScale: [293.66, 329.63, 392.00, 440.00, 523.25, 587.33] // D4, E4, G4, A4, C5, D5
  },
  // 2. Fmaj9 / A (Luminous morning light over Als fjord)
  {
    bass: 87.31, // F2
    pad: [174.61, 220.00, 261.63, 329.63], // F3, A3, C4, E4
    melodyScale: [329.63, 349.23, 392.00, 440.00, 523.25, 659.25] // E4, F4, G4, A4, C5, E5
  },
  // 3. Cadd9 / G (Pure coastal air & botanicals)
  {
    bass: 65.41, // C2
    pad: [130.81, 196.00, 246.94, 293.66], // C3, G3, B3, D4
    melodyScale: [261.63, 293.66, 329.63, 392.00, 493.88, 587.33] // C4, D4, E4, G4, B4, D5
  },
  // 4. Am9 / E (Artisan craft & evening embers)
  {
    bass: 55.00, // A1
    pad: [110.00, 164.81, 220.00, 261.63, 329.63], // A2, E3, A3, C4, E4
    melodyScale: [220.00, 261.63, 293.66, 329.63, 392.00, 440.00, 523.25] // A3, C4, D4, E4, G4, A4, C5
  }
];

// Rich, silky acoustic chime tones for hover feedback (Pentatonic D-major/minor celestial scale)
const LUXURY_HOVER_PENTATONIC = [
  293.66, // D4
  329.63, // E4
  392.00, // G4
  440.00, // A4
  523.25, // C5
  587.33, // D5
  659.25  // E5
];

// Global Sound FX active state (defaults to true)
let isSoundFXEnabled = (() => {
  try {
    const saved = localStorage.getItem('kalas_sound_fx');
    return saved !== null ? saved === 'true' : true;
  } catch (_) {
    return true;
  }
})();

// Ambient crossfade ratio (0 = hero video active, 1 = page music active)
let currentAmbientVolumeRatio = typeof window !== 'undefined' && window.scrollY > 400 ? 1 : 0;

export function initAudio() {
  if (audioCtx) {
    if (audioCtx.state === 'suspended') {
      audioCtx.resume().catch(() => {});
    }
    if (isSoundFXEnabled && !isMusicPlaying) {
      startBackgroundMusic();
    }
    return audioCtx;
  }

  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return null;

  try {
    audioCtx = new AudioContextClass();

    // Master Gain
    masterGain = audioCtx.createGain();
    masterGain.gain.setValueAtTime(MASTER_VOLUME, audioCtx.currentTime);
    masterGain.connect(audioCtx.destination);

    // Dedicated SFX sub-bus
    sfxGain = audioCtx.createGain();
    sfxGain.gain.setValueAtTime(SFX_VOLUME, audioCtx.currentTime);
    sfxGain.connect(masterGain);

    // Dedicated calm background music sub-bus
    musicGain = audioCtx.createGain();
    musicGain.gain.setValueAtTime(MUSIC_VOLUME * currentAmbientVolumeRatio, audioCtx.currentTime);
    musicGain.connect(masterGain);

    if (isSoundFXEnabled) {
      startBackgroundMusic();
    }

    return audioCtx;
  } catch (_) {
    return null;
  }
}

export function getSoundFXEnabled() {
  return isSoundFXEnabled;
}

export function setSoundFXEnabled(enabled) {
  isSoundFXEnabled = Boolean(enabled);
  try {
    localStorage.setItem('kalas_sound_fx', String(isSoundFXEnabled));
  } catch (_) {}

  // Notify listeners
  listeners.forEach((fn) => {
    try {
      fn(isSoundFXEnabled);
    } catch (_) {}
  });

  if (isSoundFXEnabled) {
    initAudio();
    playToggleSound(true);
    startBackgroundMusic();
  } else {
    playToggleSound(false);
    stopBackgroundMusic();
  }
}

export function toggleSoundFX() {
  const next = !isSoundFXEnabled;
  setSoundFXEnabled(next);
  return next;
}

export function subscribeSoundFX(callback) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

/**
 * Smoothly sets the page ambient music volume ratio (0.0 to 1.0)
 * Used to crossfade between hero video audio and page ambient audio on scroll.
 */
export function setAmbientMusicVolumeRatio(ratio, rampDuration = 0.3) {
  const clampedRatio = Math.max(0, Math.min(1, Number(ratio) || 0));
  currentAmbientVolumeRatio = clampedRatio;

  if (!isSoundFXEnabled) return;

  if (!audioCtx) {
    if (clampedRatio > 0.05) {
      initAudio();
    }
    return;
  }

  if (clampedRatio > 0.01 && !isMusicPlaying) {
    startBackgroundMusic();
  }

  if (musicGain && audioCtx) {
    try {
      const t = audioCtx.currentTime;
      const targetGain = MUSIC_VOLUME * clampedRatio;
      musicGain.gain.cancelScheduledValues(t);
      musicGain.gain.setValueAtTime(Math.max(0.0001, musicGain.gain.value), t);
      musicGain.gain.linearRampToValueAtTime(Math.max(0.0001, targetGain), t + Math.max(0.05, rampDuration));
    } catch (_) {}
  }
}

/**
 * Starts the calm, alive, musical Nordic acoustic background track.
 * Plays organic warm piano/celeste arpeggios, gentle breathing pad layers,
 * and soothing sparse melodic bells.
 */
function startBackgroundMusic() {
  if (!audioCtx || isMusicPlaying || !isSoundFXEnabled) return;

  try {
    if (audioCtx.state === 'suspended') {
      audioCtx.resume().catch(() => {});
    }

    isMusicPlaying = true;
    currentHarmonicStep = 0;

    // Smooth entry fade
    const t = audioCtx.currentTime;
    if (musicGain) {
      const targetVol = MUSIC_VOLUME * currentAmbientVolumeRatio;
      musicGain.gain.cancelScheduledValues(t);
      musicGain.gain.setValueAtTime(0.0001, t);
      musicGain.gain.linearRampToValueAtTime(Math.max(0.0001, targetVol), t + 1.8);
    }

    // Schedule evolving harmonic cycles (each chord lasts 8.8 seconds with silky crossfade)
    function scheduleNextChord() {
      if (!isMusicPlaying || !audioCtx || !isSoundFXEnabled) return;

      const currentHarmony = NORDIC_HARMONIES[currentHarmonicStep];
      playCalmWarmChord(currentHarmony);

      currentHarmonicStep = (currentHarmonicStep + 1) % NORDIC_HARMONIES.length;
      musicScheduleTimer = setTimeout(scheduleNextChord, 8400);
    }

    // Schedule lively, gentle acoustic piano/celeste melodic notes (peacefully spaced)
    function scheduleMelodicMotif() {
      if (!isMusicPlaying || !audioCtx || !isSoundFXEnabled) return;

      const currentHarmony = NORDIC_HARMONIES[currentHarmonicStep];
      playAcousticMelodyNote(currentHarmony.melodyScale);

      // Relaxing natural human-like tempo between 1.8s and 3.8s
      const nextDelay = 1800 + Math.random() * 2000;
      melodyScheduleTimer = setTimeout(scheduleMelodicMotif, nextDelay);
    }

    scheduleNextChord();
    melodyScheduleTimer = setTimeout(scheduleMelodicMotif, 800);
  } catch (_) {
    isMusicPlaying = false;
  }
}

/**
 * Renders a warm, calm, soothing harmonic pad (silky pure sines with slow breath, no buzzing)
 */
function playCalmWarmChord(harmony) {
  if (!audioCtx || !musicGain || !isMusicPlaying) return;

  try {
    const t = audioCtx.currentTime;
    const duration = 9.2; // Overlaps gently with the next chord for seamless ambient flow
    const nodes = [];

    // 1. Warm Master Lowpass Filter with gentle organic breathing
    const filter = audioCtx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(380, t);
    filter.frequency.linearRampToValueAtTime(540, t + duration * 0.45);
    filter.frequency.linearRampToValueAtTime(360, t + duration);
    filter.Q.setValueAtTime(0.5, t);
    filter.connect(musicGain);

    // 2. Soft, warm sub-bass root (pure sine, clean & subtle)
    const bassOsc = audioCtx.createOscillator();
    const bassGain = audioCtx.createGain();
    bassOsc.type = 'sine';
    bassOsc.frequency.setValueAtTime(harmony.bass, t);

    bassGain.gain.setValueAtTime(0.0001, t);
    bassGain.gain.linearRampToValueAtTime(0.038, t + 2.4); // Gentle 2.4s swell
    bassGain.gain.setValueAtTime(0.038, t + duration - 2.6);
    bassGain.gain.linearRampToValueAtTime(0.0001, t + duration);

    bassOsc.connect(bassGain);
    bassGain.connect(filter);
    bassOsc.start(t);
    bassOsc.stop(t + duration);
    nodes.push(bassOsc, bassGain);

    // 3. Warm mid-range pad notes (pure sines, soothing intervals)
    harmony.pad.forEach((freq, idx) => {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, t);

      // Subtle volume distribution per voice
      const vGain = 0.020 - idx * 0.0025;

      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.linearRampToValueAtTime(Math.max(0.001, vGain), t + 2.4 + idx * 0.25);
      gain.gain.setValueAtTime(Math.max(0.001, vGain), t + duration - 2.4);
      gain.gain.linearRampToValueAtTime(0.0001, t + duration);

      osc.connect(gain);
      gain.connect(filter);
      osc.start(t);
      osc.stop(t + duration);
      nodes.push(osc, gain);
    });

    activeMusicNodes.push({ nodes, filter });

    // Garbage collection after chord completes
    setTimeout(() => {
      activeMusicNodes = activeMusicNodes.filter((item) => item.filter !== filter);
      try {
        filter.disconnect();
      } catch (_) {}
    }, duration * 1000 + 300);
  } catch (_) {}
}

/**
 * Plays a single calm, lovely acoustic piano / celeste / bell note
 * Creates an alive, gentle Scandinavian melody that breathes peacefully.
 */
function playAcousticMelodyNote(scale) {
  if (!audioCtx || !musicGain || !isMusicPlaying) return;

  try {
    const t = audioCtx.currentTime;
    const freq = scale[Math.floor(Math.random() * scale.length)];

    // Acoustic Celeste / Rhodes voice: Fundamental + subtle warm 2nd harmonic
    const fundamental = audioCtx.createOscillator();
    const harmonic = audioCtx.createOscillator();
    const voiceGain = audioCtx.createGain();
    const filter = audioCtx.createBiquadFilter();

    fundamental.type = 'sine';
    fundamental.frequency.setValueAtTime(freq, t);

    harmonic.type = 'sine';
    harmonic.frequency.setValueAtTime(freq * 2, t); // Octave overtone for acoustic bell warmth

    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(freq * 3.0, t);
    filter.frequency.exponentialRampToValueAtTime(freq * 0.8, t + 2.6);

    // Natural acoustic envelope: soft hammer transient, warm ringing sustain, gentle decay
    const noteVolume = 0.024 + Math.random() * 0.010;
    voiceGain.gain.setValueAtTime(0.0001, t);
    voiceGain.gain.linearRampToValueAtTime(noteVolume, t + 0.015); // Soft 15ms hammer attack
    voiceGain.gain.exponentialRampToValueAtTime(noteVolume * 0.32, t + 0.7);
    voiceGain.gain.exponentialRampToValueAtTime(0.0001, t + 2.8); // 2.8s peaceful ring-out

    fundamental.connect(filter);
    harmonic.connect(filter);
    filter.connect(voiceGain);
    voiceGain.connect(musicGain);

    fundamental.start(t);
    harmonic.start(t);
    fundamental.stop(t + 2.9);
    harmonic.stop(t + 2.9);
  } catch (_) {}
}

/**
 * Gracefully stops the ambient background music
 */
function stopBackgroundMusic() {
  isMusicPlaying = false;
  if (musicScheduleTimer) {
    clearTimeout(musicScheduleTimer);
    musicScheduleTimer = null;
  }
  if (melodyScheduleTimer) {
    clearTimeout(melodyScheduleTimer);
    melodyScheduleTimer = null;
  }

  if (!musicGain || !audioCtx) return;

  try {
    const t = audioCtx.currentTime;
    musicGain.gain.cancelScheduledValues(t);
    musicGain.gain.setValueAtTime(musicGain.gain.value, t);
    musicGain.gain.linearRampToValueAtTime(0.0001, t + 0.8);

    setTimeout(() => {
      activeMusicNodes.forEach(({ nodes, filter }) => {
        nodes.forEach((node) => {
          try {
            if (node.stop) node.stop();
            node.disconnect();
          } catch (_) {}
        });
        try {
          filter.disconnect();
        } catch (_) {}
      });
      activeMusicNodes = [];
    }, 900);
  } catch (_) {}
}

/**
 * LUXURY HOVER SOUND:
 * Pure, warm Nordic acoustic crystal & copper harmonic chime.
 * Delicate pentatonic note with silky dampening and zero harshness.
 */
export function playHoverSound() {
  if (!isSoundFXEnabled) return;
  const now = Date.now();
  if (now - lastHoverTime < 90) return; // Musical pacing throttle
  lastHoverTime = now;

  try {
    const ctx = initAudio();
    if (!ctx) return;

    const t = ctx.currentTime;
    const noteFreq = LUXURY_HOVER_PENTATONIC[hoverToneIndex % LUXURY_HOVER_PENTATONIC.length];
    hoverToneIndex = (hoverToneIndex + 1) % LUXURY_HOVER_PENTATONIC.length;

    // Pure sine acoustic chime (warm glass/copper tone)
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(noteFreq, t);

    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(noteFreq * 2.2, t);
    filter.frequency.exponentialRampToValueAtTime(noteFreq * 0.8, t + 0.28);

    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.linearRampToValueAtTime(0.038, t + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.30);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(sfxGain || masterGain || ctx.destination);

    osc.start(t);
    osc.stop(t + 0.32);
  } catch (_) {}
}

/**
 * LUXURY CLICK SOUND:
 * Tactile, soft, organic acoustic closure — warm wooden drop & soft copper click.
 */
export function playClickSound() {
  if (!isSoundFXEnabled) return;

  try {
    const ctx = initAudio();
    if (!ctx) return;

    const t = ctx.currentTime;

    const bodyOsc = ctx.createOscillator();
    const bodyGain = ctx.createGain();
    const bodyFilter = ctx.createBiquadFilter();

    bodyOsc.type = 'sine';
    bodyOsc.frequency.setValueAtTime(150, t);
    bodyOsc.frequency.exponentialRampToValueAtTime(55, t + 0.05);

    bodyFilter.type = 'lowpass';
    bodyFilter.frequency.setValueAtTime(220, t);

    bodyGain.gain.setValueAtTime(0.0001, t);
    bodyGain.gain.linearRampToValueAtTime(0.055, t + 0.003);
    bodyGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.06);

    bodyOsc.connect(bodyFilter);
    bodyFilter.connect(bodyGain);
    bodyGain.connect(sfxGain || masterGain || ctx.destination);

    bodyOsc.start(t);
    bodyOsc.stop(t + 0.065);
  } catch (_) {}
}

/**
 * Calm, subtle breath of air when smoothly surfing with the mouse
 */
export function playSurfingSound(speedRatio = 1) {
  if (!isSoundFXEnabled) return;
  const now = Date.now();
  if (now - lastSurfingTime < 1800) return; // Non-fatiguing pacing
  lastSurfingTime = now;

  try {
    const ctx = initAudio();
    if (!ctx) return;

    const t = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(220.0, t); // A3

    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(320, t);
    filter.frequency.exponentialRampToValueAtTime(180, t + 0.7);

    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.linearRampToValueAtTime(0.012 * Math.min(speedRatio, 1.2), t + 0.25);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.75);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(sfxGain || masterGain || ctx.destination);

    osc.start(t);
    osc.stop(t + 0.8);
  } catch (_) {}
}

/**
 * Toggle on/off feedback sound
 */
export function playToggleSound(activating) {
  try {
    const ctx = initAudio();
    if (!ctx) return;

    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    if (activating) {
      osc.frequency.setValueAtTime(329.63, t); // E4
      osc.frequency.exponentialRampToValueAtTime(523.25, t + 0.15); // C5
    } else {
      osc.frequency.setValueAtTime(440.00, t); // A4
      osc.frequency.exponentialRampToValueAtTime(261.63, t + 0.15); // C4
    }

    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.linearRampToValueAtTime(0.040, t + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.18);

    osc.connect(gain);
    gain.connect(sfxGain || masterGain || ctx.destination);

    osc.start(t);
    osc.stop(t + 0.2);
  } catch (_) {}
}
